import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, Loader2, PlaneTakeoff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import { TravelItineraryCards } from '@/app/components/travel/travel-itinerary';
import { useConfirmMyTravel, useMyTravelItinerary } from '@/hooks/use-my-travel';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { toast } from '@/app/components/ui/simple-toaster';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

export function MyTravelItineraryDetailPage() {
  const orgTimeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const itineraryId = Number(id);
  const { data: itinerary, isLoading, isError } = useMyTravelItinerary(itineraryId);
  const confirmTravel = useConfirmMyTravel();

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Skeleton className="h-10 w-52" />
        <Skeleton className="mt-6 h-64" />
      </div>
    );
  }

  if (isError || !itinerary) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card>
          <CardContent className="py-12 text-center text-sm text-slate-500">
            Travel itinerary not found.
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleConfirm = async () => {
    try {
      await confirmTravel.mutateAsync(itinerary.id);
      toast.success('Travel confirmed — thank you.');
    } catch {
      toast.error('Failed to confirm travel.');
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto">
      <Link
        to="/my-dashboard"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to dashboard
      </Link>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <PlaneTakeoff className="h-5 w-5 text-slate-500" />
            Travel status
          </CardTitle>
          <CardDescription>{itinerary.project_name}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              className={
                itinerary.is_confirmed
                  ? 'bg-emerald-100 text-emerald-800'
                  : 'bg-amber-100 text-amber-800'
              }
            >
              {itinerary.is_confirmed ? 'Confirmed' : 'Unconfirmed'}
            </Badge>
            {itinerary.is_confirmed && itinerary.acknowledged_at && (
              <span className="text-sm text-slate-500">
                Confirmed {formatDateTimeInTimeZone(itinerary.acknowledged_at, orgTimeZone)}
              </span>
            )}
          </div>
          {!itinerary.is_confirmed && (
            <Button size="lg" disabled={confirmTravel.isPending} onClick={handleConfirm}>
              {confirmTravel.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <BadgeCheck className="mr-2 h-4 w-4" />
              )}
              Confirm — I have my travel
            </Button>
          )}
        </CardContent>
      </Card>

      <TravelItineraryCards itinerary={itinerary} />
    </div>
  );
}
